import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { 
  AdMetadata,
  FileWithStatus, 
  StepValidationResult 
} from '../types';
import { UploadStep, UploadWizardState, CreativeGroup, TagMetadata, CreativeAssignmentMode, ExistingAssignment } from '../types/upload';

interface UploadWizardContextType {
  // State
  state: UploadWizardState;
  
  // Navigation
  currentStep: UploadStep;
  canGoNext: boolean;
  canGoPrevious: boolean;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (step: UploadStep) => void;
  
  // State updates
  setFiles: (files: FileWithStatus[]) => void;
  setProductionId: (productionId: number | null) => void;
  setActorLicenseIds: (ids: string[]) => void;
  setOriginalActorLicenseIds: (ids: string[]) => void;
  setAssignmentMode: (mode: CreativeAssignmentMode) => void;
  setIndividualMetadata: (fileId: string, metadata: AdMetadata) => void;
  
  // Tag management
  setTagMetadata: (creativeNumber: number, tagData: TagMetadata) => void;
  getTagMetadata: (creativeNumber: number) => TagMetadata | null;
  
  // Creative grouping
  createCreativeGroup: (fileIds: string[], sharedMetadata: Partial<AdMetadata>) => { success: boolean; error?: string };
  updateCreativeGroup: (groupId: string, updates: Partial<CreativeGroup>) => void;
  removeFileFromGroup: (fileId: string) => void;
  unassignCreativeNumber: (creativeNumber: number) => void;
  getFileGroup: (fileId: string) => CreativeGroup | null;
  
  // Validation helpers
  validateRatiosInGroup: (fileIds: string[]) => { isValid: boolean; duplicateRatios: string[] };
  getCreativeGroups: () => Map<number, { files: Array<{ file: FileWithStatus; metadata: AdMetadata }>; name: string; ratios: string[] }>;
  
  // Validation
  validateCurrentStep: () => StepValidationResult;
  setStepValidation: (step: UploadStep, isValid: boolean) => void;
  
  // Reset
  resetWizard: () => void;
}

const UploadWizardContext = createContext<UploadWizardContextType | undefined>(undefined);

interface UploadWizardProviderProps {
  children: ReactNode;
}

const initialState: UploadWizardState = {
  currentStep: UploadStep.FILE_SELECTION,
  files: [],
  productionId: null,
  assignmentMode: CreativeAssignmentMode.NEW,
  existingAssignments: new Map<string, ExistingAssignment>(),
  individualMetadata: new Map(),
  creativeGroups: new Map(),
  tagMetadata: new Map(),
  actorLicenseIds: [],
  originalActorLicenseIds: [],
  stepValidation: {
    [UploadStep.FILE_SELECTION]: false,
    [UploadStep.PRODUCTION_SELECTION]: false,
    [UploadStep.INDIVIDUAL_METADATA]: false,
    [UploadStep.TAG]: false,
    [UploadStep.REVIEW_UPLOAD]: false,
  },
  isUploading: false,
};

export const UploadWizardProvider: React.FC<UploadWizardProviderProps> = ({ children }) => {
  const [state, setState] = useState<UploadWizardState>(initialState);

  // Helper function to get step order
  const getStepOrder = (step: UploadStep): number => {
    const steps = [
      UploadStep.FILE_SELECTION,
      UploadStep.PRODUCTION_SELECTION,
      UploadStep.INDIVIDUAL_METADATA,
      UploadStep.TAG,
      UploadStep.REVIEW_UPLOAD
    ];
    return steps.indexOf(step);
  };

  // Navigation helpers
  const canGoNext = useCallback(() => {
    // Can't go next if current step is not valid
    if (!state.stepValidation[state.currentStep]) {
      return false;
    }
    
    // Can't go next from the last step
    if (state.currentStep === UploadStep.REVIEW_UPLOAD) {
      return false;
    }
    
    return true;
  }, [state.stepValidation, state.currentStep]);

  const canGoPrevious = useCallback(() => {
    return state.currentStep !== UploadStep.FILE_SELECTION && !state.isUploading;
  }, [state.currentStep, state.isUploading]);

  const getNextStep = (currentStep: UploadStep): UploadStep => {
    switch (currentStep) {
      case UploadStep.FILE_SELECTION:
        return UploadStep.PRODUCTION_SELECTION;
      case UploadStep.PRODUCTION_SELECTION:
        // If assigning to existing creatives, skip metadata and tagging
        if (state.assignmentMode === CreativeAssignmentMode.EXISTING) {
          return UploadStep.REVIEW_UPLOAD;
        }
        return UploadStep.INDIVIDUAL_METADATA;
      case UploadStep.INDIVIDUAL_METADATA:
        return UploadStep.TAG;
      case UploadStep.TAG:
        return UploadStep.REVIEW_UPLOAD;
      default:
        return currentStep;
    }
  };

  const getPreviousStep = (currentStep: UploadStep): UploadStep => {
    switch (currentStep) {
      case UploadStep.PRODUCTION_SELECTION:
        return UploadStep.FILE_SELECTION;
      case UploadStep.INDIVIDUAL_METADATA:
        return UploadStep.PRODUCTION_SELECTION;
      case UploadStep.TAG:
        return UploadStep.INDIVIDUAL_METADATA;
      case UploadStep.REVIEW_UPLOAD:
        return UploadStep.TAG;
      default:
        return currentStep;
    }
  };

  // Navigation functions
  const goToNextStep = useCallback(() => {
    if (!canGoNext()) return;
    
    const nextStep = getNextStep(state.currentStep);
    setState(prev => ({ ...prev, currentStep: nextStep }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentStep, canGoNext]);

  const goToPreviousStep = useCallback(() => {
    if (!canGoPrevious()) return;
    
    const prevStep = getPreviousStep(state.currentStep);
    setState(prev => ({ ...prev, currentStep: prevStep }));
  }, [state.currentStep, canGoPrevious]);

  const goToStep = useCallback((step: UploadStep) => {
    // Only allow going to steps that are before the current one or have been validated
    const targetOrder = getStepOrder(step);
    const currentOrder = getStepOrder(state.currentStep);
    
    if (targetOrder <= currentOrder || state.stepValidation[step]) {
      setState(prev => ({ ...prev, currentStep: step }));
    }
  }, [state.currentStep, state.stepValidation]);

  // State setters
  const setFiles = useCallback((files: FileWithStatus[]) => {
    setState(prev => ({ 
      ...prev, 
      files,
      // Reset individual metadata, groups, and tags when files change
      individualMetadata: new Map(),
      creativeGroups: new Map(),
      tagMetadata: new Map(),
      actorLicenseIds: [],
      originalActorLicenseIds: [],
      existingAssignments: new Map<string, ExistingAssignment>()
    }));
  }, []);

  const setProductionId = useCallback((productionId: number | null) => {
    setState(prev => ({ ...prev, productionId }));
  }, []);

  const setActorLicenseIds = useCallback((actorLicenseIds: string[]) => {
    setState(prev => ({ ...prev, actorLicenseIds }));
  }, []);

  const setOriginalActorLicenseIds = useCallback((originalActorLicenseIds: string[]) => {
    setState(prev => ({ ...prev, originalActorLicenseIds }));
  }, []);

  const setAssignmentMode = useCallback((mode: CreativeAssignmentMode) => {
    setState(prev => ({
      ...prev,
      assignmentMode: mode,
      // Clear metadata and groupings when switching modes to avoid cross-mode residue
      individualMetadata: new Map(),
      creativeGroups: new Map(),
      existingAssignments: new Map<string, ExistingAssignment>()
    }));
  }, []);

  const setIndividualMetadata = useCallback((fileId: string, metadata: AdMetadata) => {
    setState(prev => {
      const newIndividualMetadata = new Map(prev.individualMetadata);
      newIndividualMetadata.set(fileId, metadata);
      return { ...prev, individualMetadata: newIndividualMetadata };
    });
  }, []);

  // Tag management functions
  const setTagMetadata = useCallback((creativeNumber: number, tagData: TagMetadata) => {
    setState(prev => {
      const newTagMetadata = new Map(prev.tagMetadata);
      newTagMetadata.set(creativeNumber, tagData);
      return { ...prev, tagMetadata: newTagMetadata };
    });
  }, []);

  const getTagMetadata = useCallback((creativeNumber: number): TagMetadata | null => {
    return state.tagMetadata.get(creativeNumber) || null;
  }, [state.tagMetadata]);

  // Validation helper for ratios
  const validateRatiosInGroup = useCallback((fileIds: string[]): { isValid: boolean; duplicateRatios: string[] } => {
    const ratios: string[] = [];
    const seenRatios = new Set<string>();
    const duplicateRatios: string[] = [];

    fileIds.forEach(fileId => {
      const file = state.files.find(f => f.id === fileId);
      if (file) {
        const ratio = detectRatio(file);
        if (seenRatios.has(ratio)) {
          if (!duplicateRatios.includes(ratio)) {
            duplicateRatios.push(ratio);
          }
        } else {
          seenRatios.add(ratio);
        }
        ratios.push(ratio);
      }
    });

    return {
      isValid: duplicateRatios.length === 0,
      duplicateRatios
    };
  }, [state.files]);

  // Helper to get actual creative groups based on metadata
  const getCreativeGroups = useCallback(() => {
    const groups = new Map<number, { files: Array<{ file: FileWithStatus; metadata: AdMetadata }>; name: string; ratios: string[] }>();
    
    state.files.forEach(file => {
      const metadata = state.individualMetadata.get(file.id);
      if (metadata) {
        const creativeNumber = metadata.creative_number;
        if (!groups.has(creativeNumber)) {
          groups.set(creativeNumber, { 
            files: [], 
            name: metadata.name_en,
            ratios: []
          });
        }
        const group = groups.get(creativeNumber)!;
        group.files.push({ file, metadata });
        if (!group.ratios.includes(metadata.ratio)) {
          group.ratios.push(metadata.ratio);
        }
      }
    });
    
    return groups;
  }, [state.files, state.individualMetadata]);

  // Creative grouping functions
  const createCreativeGroup = useCallback((fileIds: string[], sharedMetadata: Partial<AdMetadata>): { success: boolean; error?: string } => {
    // Validate ratios first (only if more than one file)
    if (fileIds.length > 1) {
      const validation = validateRatiosInGroup(fileIds);
      if (!validation.isValid) {
        return {
          success: false,
          error: `Cannot group files with duplicate ratios: ${validation.duplicateRatios.join(', ')}`
        };
      }
    }

    const groupId = `group_${Date.now()}`;
    const creativeNumber = sharedMetadata.creative_number; // Should be provided from the dialog
    if (!creativeNumber) {
      return {
        success: false,
        error: 'Creative number is required'
      };
    }
    const groupName = `Creative ${creativeNumber}`;
    
    setState(prev => {
      const newGroups = new Map(prev.creativeGroups);
      const newGroup: CreativeGroup = {
        id: groupId,
        name: groupName,
        creativeNumber,
        fileIds,
        metadata: {
          name_en: sharedMetadata.name_en || groupName,
          language: sharedMetadata.language || 'EN',
          watchers: sharedMetadata.watchers || [],
          created_by: sharedMetadata.created_by || 'web_user',
        }
      };
      newGroups.set(groupId, newGroup);

      // Update individual metadata for grouped files
      const newIndividualMetadata = new Map(prev.individualMetadata);
      fileIds.forEach(fileId => {
        const file = prev.files.find(f => f.id === fileId);
        if (file) {
          const metadata: AdMetadata = {
            production_id: prev.productionId!,
            creative_number: creativeNumber,
            ratio: detectRatio(file),
            name_en: sharedMetadata.name_en || groupName,
            language: sharedMetadata.language || 'EN',
            filename: file.name,
            created_by: sharedMetadata.created_by || 'web_user',
            watchers: sharedMetadata.watchers || [],
            duration: file.preview?.duration,
          };
          newIndividualMetadata.set(fileId, metadata);
        }
      });

      return { 
        ...prev, 
        creativeGroups: newGroups,
        individualMetadata: newIndividualMetadata
      };
    });

    return { success: true };
  }, [validateRatiosInGroup]);

  const updateCreativeGroup = useCallback((groupId: string, updates: Partial<CreativeGroup>) => {
    setState(prev => {
      const newGroups = new Map(prev.creativeGroups);
      const existingGroup = newGroups.get(groupId);
      if (existingGroup) {
        const updatedGroup = { ...existingGroup, ...updates };
        newGroups.set(groupId, updatedGroup);
        
        // Update individual metadata for all files in the group
        const newIndividualMetadata = new Map(prev.individualMetadata);
        existingGroup.fileIds.forEach(fileId => {
          const metadata = newIndividualMetadata.get(fileId);
          if (metadata) {
            newIndividualMetadata.set(fileId, {
              ...metadata,
              creative_number: updatedGroup.creativeNumber,
              name_en: updatedGroup.metadata.name_en,
              language: updatedGroup.metadata.language,
              watchers: updatedGroup.metadata.watchers,
              created_by: updatedGroup.metadata.created_by,
            });
          }
        });
        
        return { 
          ...prev, 
          creativeGroups: newGroups,
          individualMetadata: newIndividualMetadata
        };
      }
      return prev;
    });
  }, []);

  const removeFileFromGroup = useCallback((fileId: string) => {
    setState(prev => {
      const newGroups = new Map(prev.creativeGroups);
      
      // Find and update the group that contains this file
      for (const [groupId, group] of newGroups.entries()) {
        if (group.fileIds.includes(fileId)) {
          const updatedFileIds = group.fileIds.filter(id => id !== fileId);
          
          if (updatedFileIds.length === 0) {
            // Remove empty group
            newGroups.delete(groupId);
          } else {
            // Update group with remaining files
            newGroups.set(groupId, { ...group, fileIds: updatedFileIds });
          }
          break;
        }
      }
      
      return { ...prev, creativeGroups: newGroups };
    });
  }, []);

  const unassignCreativeNumber = useCallback((creativeNumber: number) => {
    setState(prev => {
      // Clear individual metadata entries that belong to this creative number
      const newIndividualMetadata = new Map(prev.individualMetadata);
      for (const [fileId, metadata] of newIndividualMetadata.entries()) {
        if (metadata.creative_number === creativeNumber) {
          newIndividualMetadata.delete(fileId);
        }
      }

      // Also remove any explicit creativeGroups entries that match (defensive)
      const newGroups = new Map(prev.creativeGroups);
      for (const [groupId, group] of newGroups.entries()) {
        if (group.creativeNumber === creativeNumber) {
          newGroups.delete(groupId);
        }
      }

      return { ...prev, individualMetadata: newIndividualMetadata, creativeGroups: newGroups };
    });
  }, []);

  const getFileGroup = useCallback((fileId: string): CreativeGroup | null => {
    for (const group of state.creativeGroups.values()) {
      if (group.fileIds.includes(fileId)) {
        return group;
      }
    }
    return null;
  }, [state.creativeGroups]);

  // Helper function to detect video ratio
  const detectRatio = (file: FileWithStatus): string => {
    if (file.preview?.width && file.preview?.height) {
      const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
      const divisor = gcd(file.preview.width, file.preview.height);
      return `${file.preview.width / divisor}:${file.preview.height / divisor}`;
    }
    return '16:9'; // Default ratio
  };

  // Validation
  const validateCurrentStep = useCallback((): StepValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];

    switch (state.currentStep) {
      case UploadStep.FILE_SELECTION:
        if (state.files.length === 0) {
          errors.push('Please select at least one file');
        }
        break;

      case UploadStep.PRODUCTION_SELECTION:
        if (!state.productionId || state.productionId <= 0) {
          errors.push('Please enter a valid production ID');
        }
        // If assigning to existing creatives, ensure each file has a valid assignment (metadata prepared)
        if (state.assignmentMode === CreativeAssignmentMode.EXISTING) {
          const requiredFiles = state.files.length;
          const completed = state.files.filter(file => {
            const metadata = state.individualMetadata.get(file.id);
            return metadata && metadata.production_id && metadata.creative_number && metadata.ratio && metadata.name_en && metadata.language && metadata.created_by;
          }).length;
          if (completed < requiredFiles) {
            errors.push(`${requiredFiles - completed} file(s) still need valid creative assignments`);
          }
        }
        break;

      case UploadStep.INDIVIDUAL_METADATA:
        if (state.assignmentMode !== CreativeAssignmentMode.EXISTING) {
          const requiredFields = state.files.length;
          const completedFiles = state.files.filter(file => {
            const metadata = state.individualMetadata.get(file.id);
            return metadata && metadata.production_id && metadata.creative_number && 
                   metadata.ratio && metadata.name_en && metadata.language && metadata.created_by;
          }).length;
          
          if (completedFiles < requiredFields) {
            errors.push(`${requiredFields - completedFiles} file(s) still need complete metadata`);
          }
        }
        break;

      case UploadStep.TAG:
        const creativeGroups = getCreativeGroups();
        const totalCreatives = creativeGroups.size;
        const taggedCreatives = Array.from(creativeGroups.keys()).filter(creativeNumber => {
          const tagData = state.tagMetadata.get(creativeNumber);
          return tagData && tagData.cast && tagData.heroAge && tagData.heroGender;
        }).length;
        
        if (taggedCreatives < totalCreatives) {
          errors.push(`${totalCreatives - taggedCreatives} creative(s) still need complete tag information`);
        }
        break;

      case UploadStep.REVIEW_UPLOAD:
        // Final validation - check all previous steps
        if (state.files.length === 0) errors.push('No files selected');
        if (!state.productionId) errors.push('No production ID set');
        break;
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const setStepValidation = useCallback((step: UploadStep, isValid: boolean) => {
    setState(prev => ({
      ...prev,
      stepValidation: {
        ...prev.stepValidation,
        [step]: isValid
      }
    }));
  }, []);

  const resetWizard = useCallback(() => {
    setState(initialState);
  }, []);

  const contextValue: UploadWizardContextType = {
    // State
    state,
    
    // Navigation
    currentStep: state.currentStep,
    canGoNext: canGoNext(),
    canGoPrevious: canGoPrevious(),
    goToNextStep,
    goToPreviousStep,
    goToStep,
    
    // State updates
    setFiles,
    setProductionId,
    setActorLicenseIds,
    setOriginalActorLicenseIds,
    setAssignmentMode,
    setIndividualMetadata,
    
    // Tag management
    setTagMetadata,
    getTagMetadata,
    
    // Creative grouping
    createCreativeGroup,
    updateCreativeGroup,
    removeFileFromGroup,
    unassignCreativeNumber,
    getFileGroup,
    
    // Validation helpers
    validateRatiosInGroup,
    getCreativeGroups,
    
    // Validation
    validateCurrentStep,
    setStepValidation,
    
    // Reset
    resetWizard,
  };

  return (
    <UploadWizardContext.Provider value={contextValue}>
      {children}
    </UploadWizardContext.Provider>
  );
};

export const useUploadWizard = (): UploadWizardContextType => {
  const context = useContext(UploadWizardContext);
  if (context === undefined) {
    throw new Error('useUploadWizard must be used within an Upload2WizardProvider');
  }
  return context;
};